/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  reportField,
  reportIdField,
  SCHEDULE_TYPE_CONSTRUCTION,
  SCHEDULE_TYPE_MAINTENANCE,
  SCHEDULE_TYPE_PRE_CONSTRUCTION,
  SCHEDULE_TYPE_SALES,
  ScheduleTaskType,
  ScheduleTemplateListFilter,
  ScheduleTemplateRestService,
  TaskRelationType,
} from '@gh/builder-shared-data';
import {
  DictionaryService,
  getRouteParam,
  getRouteQueryParam,
  getRouteStaticData,
  InitViewService,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { GridLayoutService } from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';
import { GanttChartLoader, GanttExtension } from '@gh/builder-shared-gantt-chart';
import { Observable } from 'rxjs';
import {
  ScheduleTemplateGanttDetails,
  scheduleTemplateGanttDetailsFromDto,
  scheduleTemplateGanttDetailsToDto,
} from './schedule-template-gantt-details.model';
import { FilterNames, FilterRestService, REPORT_SCH_TEMPLATE, ReportService } from '@gh/builder-shared';
import { ScheduleTemplateDetails } from './scheduling-schedule-template.model';
import { assign } from 'lodash';

@Injectable()
export class ScheduleTemplateGanttDetailsBusinessService implements InitViewService {

  constructor(private gridLayouts: GridLayoutService,
              private dictionaries: DictionaryService,
              private reportService: ReportService,
              private filterService: FilterRestService,
              private scheduleTemplateRest: ScheduleTemplateRestService,
              private ganttChartLoader: GanttChartLoader) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    const cloneScheduleTemplateId = getRouteQueryParam(route, 'cloneScheduleTemplate');
    const templateId = getRouteParam(route, 'templateId');
    const scheduleTemplateType = getRouteStaticData(route)['type'];
    return {
      gantt: this.ganttChartLoader.load({
        extensions: [
          GanttExtension.SmartRendering,
          GanttExtension.AutoScheduling,
          GanttExtension.Tooltip,
          GanttExtension.Marker,
          GanttExtension.ExportPdf,
        ],
      }),
      details: !this.isNewTemplate(templateId)
        ? this.get(templateId)
        : !!cloneScheduleTemplateId
          ? this.getClone(cloneScheduleTemplateId)
          : this.init({ type: scheduleTemplateType }),
      filter: this.filterService.get(FilterNames.ganttTemplates),
      gridLayout: this.gridLayouts.get('scheduleTemplateGanttTasks'),
      taskTypeDict: this.dictionaries.getByEnum(ScheduleTaskType),
      relationshipTypeDict: this.dictionaries.getByEnum(TaskRelationType),
      reportUrl: !this.isNewTemplate(templateId) ? this.reportService.generateUrl(REPORT_SCH_TEMPLATE, [
        reportIdField('TemplateId', templateId),
        reportField('ScheduleType', {
          [SCHEDULE_TYPE_CONSTRUCTION]: 1,
          [SCHEDULE_TYPE_SALES]: 2,
          [SCHEDULE_TYPE_PRE_CONSTRUCTION]: 3,
          [SCHEDULE_TYPE_MAINTENANCE]: 4,
        }[scheduleTemplateType]),
      ]) : '',
    };
  }

  init(filter: ScheduleTemplateListFilter): Observable<ScheduleTemplateGanttDetails> {
    return this.scheduleTemplateRest.init(filter).pipe(map$(scheduleTemplateGanttDetailsFromDto));
  }

  get(scheduleId: ID): Observable<ScheduleTemplateGanttDetails> {
    return this.scheduleTemplateRest.get(scheduleId).pipe(map$(scheduleTemplateGanttDetailsFromDto));
  }

  getClone(templateId: ID): Observable<ScheduleTemplateGanttDetails> {
    return this.scheduleTemplateRest.get(templateId).pipe(
      map$(
        (scheduleTemplate: ScheduleTemplateDetails) => assign(scheduleTemplate, {
          templateId: void 0,
          name: void 0,
        })),
      map$(scheduleTemplateGanttDetailsFromDto));
  }
  update(details: ScheduleTemplateGanttDetails): Observable<ScheduleTemplateGanttDetails> {
    return this.scheduleTemplateRest.update(scheduleTemplateGanttDetailsToDto(details)).pipe(
      map$(scheduleTemplateGanttDetailsFromDto));
  }

  create(scheduleTemplate: ScheduleTemplateGanttDetails): Observable<ScheduleTemplateGanttDetails> {
    return this.scheduleTemplateRest.create(scheduleTemplateGanttDetailsToDto(scheduleTemplate)).pipe(
      map$(scheduleTemplateGanttDetailsFromDto));
  }

  saveFilter(size: any): void {
    const currentFilter = this.filterService.getLocal(FilterNames.ganttTemplates);
    this.filterService.update(FilterNames.ganttTemplates, {
      ...currentFilter,
      primary: size. primary,
    }).subscribe();
  }

  private isNewTemplate(templateId?: string): boolean {
    return !templateId || templateId === 'new';
  }
}
